import { useState, createContext, useContext, useEffect, useRef } from 'react';
import {
    Accordion,
    Button,
    Col,
    Row,
    Form,
    FormGroup,
    FormText,
    Label,
    Input,
    Modal,
    ModalBody,
} from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import {
    CheckboxInput,
    TextInput,
    currencyConfig,
} from '~/shared/components/form/Inputs';
import IntlCurrencyInput from 'react-intl-currency-input';
import { Loading } from '~/shared/components/Loading';
import { ICampaignEditInfo, MyCampaignResponse } from '~/types/campaign';
import { CampaignContext } from '~/context/campaign';
import { AuthContext } from '~/context/auth';
import {
    fetchMyCampaign,
    putMyCampaign,
    createAlbum,
    deleteAlbum,
    publishCampaign,
    republishCampaign,
} from '~/services/campaign';
import { getFilename } from '~/services/helper';
import { renderIcon } from '~/shared/components/icons';
import {
    ImageTipsCard,
    StoryTipsCard,
} from '~/shared/components/cards/TipsCards';
import { AlbumEdit } from './components/AlbumEdit';
// import { yupResolver } from '@hookform/resolvers/yup';
// import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { numberFormat } from '~/shared/components/form/Inputs';
import { RxCross2 } from 'react-icons/rx';
import { MdAdd } from 'react-icons/md';
import { CampaignPhotoUpload } from './components/CampaignPhotoUpload';
import moment from 'moment';
import TipTapEditor from '~/shared/components/tiptap/TipTapEditor';

const defaultCampaignEdit: ICampaignEditInfo = {
    goal: '',
    show_goal_bar: false,
    show_donor_roll: false,
    campaign_title: '',
    video_url: '',
    use_alternate_title: false,
    photo_url: '',
    campaign_slug: '',
    hide_location: false,
    story: '',
    alternative_links: [],
    update_photo: false,
    publish: false,
    hospital: '',
    hospital_url: '',
};

// const validationSchema = Yup.object().shape({
//   goal: Yup.number().required('Required').positive().integer(),
//   show_goal_bar: Yup.boolean(),
//   show_donor_roll: Yup.boolean(),
//   campaign_title: Yup.string().required('Required'),
//   use_alternate_title: Yup.boolean(),
//   photo_url: Yup.string().required('Required'),
//   campaign_slug: Yup.string(),
//   hospital: Yup.string(),
//   hospital_website: Yup.string().url(),
//   hide_location: Yup.boolean(),
//   story: Yup.string().required('Required'),
//   alternative_links: Yup.array().of(Yup.string().url()),
// });

// const altLinkSchema = Yup.string().url();

export const EditContext = createContext(false);

export const EditCampaign = ({}: any) => {
    const [{ user, person }, userDispatch] = useContext(AuthContext);
    const [{ campaign, needsPhotoSync }, campaignDispatch] = useContext(CampaignContext);
    const [loading, setLoading] = useState(true);
    const [savingAlbum, setSavingAlbum] = useState(false);
    const [deletingAlbum, setDeletingAlbum] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [republishing, setRepublishing] = useState(false);
    const [addLinks, setAddLinks] = useState([]);
    const [addAlbum, setAddAlbum] = useState(false);
    const [albumToDelete, setDeleteAlbum] = useState(null);
    const [newAlbumName, setNewAlbumName] = useState<string>('');
    const [campaignAlbums, setCampaignAlbums] = useState([]);
    const [editCampaignPhoto, setEditCampaignPhoto] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [isLimited, setIsLimited] = useState(false);
    const [open, setOpen] = useState<string | null>('1');
    const [showVideoField, setShowVideoField] = useState(false);
    const addLinkRef = useRef(null);
    const history = useHistory();
    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        getValues,
        register,
        formState: { errors, isSubmitting, isValid },
    } = useForm({
        defaultValues: defaultCampaignEdit,
        // resolver: yupResolver(validationSchema),
    });

    const videoUrl = watch('video_url');

    useEffect(() => {
        if (!campaign) {
            getMyCampaign();
        } else {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const access = person?.groups[0]?.name;
        if (access === 'limited team member') {
            setIsLimited(true);
        }
    }, [person]);

    useEffect(() => {
        if (campaign && !getValues('update_photo')) {
            setLoading(false);
            resetValues();

            const albums = campaign?.campaignPhotoInfo?.albums
                ? Object.values(campaign?.campaignPhotoInfo?.albums)
                : [];
            setCampaignAlbums(albums);
        }
    }, [campaign]);

    const toggle = (id: string) => {
        if (open === id) {
            setOpen(null);
        } else {
            setOpen(id);
        }
    };

    const resetValues = () => {
        if (!campaign.campaignEditInfo) {
            return;
        }
        const {
            goal,
            show_goal_bar,
            show_donor_roll,
            campaign_title,
            use_alternate_title,
            photo_url,
            campaign_slug,
            hide_location,
            alternative_links,
            story,
            video_url,
        } = campaign.campaignEditInfo;
        const campaign_id = campaign.campaignDetail.campaign_id;

        const is_publish = !!campaign?.campaignDetail?.wordpress_permalink;

        reset({
            campaign_id,
            goal,
            show_goal_bar,
            show_donor_roll,
            campaign_title,
            use_alternate_title,
            photo_url,
            campaign_slug,
            video_url,
            hide_location,
            story,
            update_photo: false,
            publish: is_publish,
        });

        if (alternative_links) {
            console.log('alternative_links: ', alternative_links);
            const alternative_link_urls = alternative_links.map((x) => x.url);
            setAddLinks(alternative_link_urls);
        }
    };

    const getMyCampaign = async () => {
        try {
            if (user?.is_superuser) {
                return;
            }
            const resp: MyCampaignResponse = await fetchMyCampaign();
            if (resp.detail && resp.detail === 'Invalid token.') {
                // Delete localStorage token
                localStorage.removeItem('token');
                history.push('/login');
            } else {
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });
                setLoading(false);
            }
        } catch (e) {
            console.log('getMyCampaign() e: ', e);
            // Show some error here
        }
    };

    const convertLinkToEmbedLink = (url: string): string => {
        const youtubeRegex =
            /(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/i;
        const vimeoRegex =
            /(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_\-]+)?/i;
        const youtubeUrl = url.match(youtubeRegex);
        const vimeoUrl = url.match(vimeoRegex);
        if (youtubeUrl && youtubeUrl[3]) {
            return `https://www.youtube.com/embed/${youtubeUrl[3]}`;
        }
        if (vimeoUrl && vimeoUrl[1]) {
            return `https://player.vimeo.com/video/${vimeoUrl[1]}`;
        }
        return '';
    };

    const getEmbeddedVideoUrl = (url: string) => {
        try {
            const youtubeRegex =
                /(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/i;
            const vimeoRegex =
                /(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_\-]+)?/i;

            // Check for YouTube URL
            const youtubeMatch = url.match(youtubeRegex);
            if (youtubeMatch && youtubeMatch[3]) {
                return `https://www.youtube.com/embed/${youtubeMatch[3]}`;
            }

            // Check for Vimeo URL
            const vimeoMatch = url.match(vimeoRegex);
            if (vimeoMatch && vimeoMatch[1]) {
                return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
            }

            // Return original URL for unsupported platforms
            return url;
        } catch (error) {
            console.error('Invalid video URL:', error);
            return url; // Fallback to the original URL
        }
    };

    useEffect(() => {
        if (videoUrl) {
            setValue('video_url', getEmbeddedVideoUrl(videoUrl));
        }
    }, [videoUrl]);

    if (!campaign) {
        return <Loading />;
    }

    const onSubmit = async (data) => {
        data['alternative_links'] = addLinks;
        try {
            if (data.video_url) {
                data.video_url = convertLinkToEmbedLink(data.video_url);
            }
            const payload = data;

            let loading;
            if (data.publish) {
                loading = toast.loading('Publishing campaign...');
            }
            const resp: MyCampaignResponse = await putMyCampaign(payload);
            if (resp.detail) {
                if (resp.detail === 'Invalid token.') {
                    // Delete localStorage token
                    localStorage.removeItem('token');
                    history.push('/login');
                } else {
                    console.log('resp', resp);
                    toast.error(resp.detail);
                }
            } else {
                const { campaignEditInfo, campaignDetail } = resp;
                const { wordpress_id, wordpress_permalink } = campaignDetail;
                campaignDispatch({
                    type: 'set_wordpress',
                    payload: { wordpress_id, wordpress_permalink },
                });
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });
                if (data.publish) {
                    toast.success('Campaign published!', {
                        id: loading,
                        duration: 6000,
                    });
                } else {
                    toast.success('Campaign updated');
                }

                reset();
            }
        } catch (e) {
            toast.dismiss();
            toast.error('There was an error saving your campaign');
            console.log('e: ', e);
        }
    };

    const handlePublishCampaign = async () => {
        try {
            setPublishing(true);
            const loadingToast = toast.loading('Publishing campaign to WordPress...');
            
            const resp = await publishCampaign(campaign.campaignDetail.campaign_id);
            
            toast.dismiss(loadingToast);
            
            // Check for standard API error (detail field)
            if (resp.detail) {
                toast.error(resp.detail);
                setPublishing(false);
                return;
            }
            
            // Check for WordPress sync error (code/message structure)
            if (resp.code && resp.message) {
                console.error('WordPress sync error:', resp);
                toast.error(resp.message);
                setPublishing(false);
                return;
            }
            
            // WordPress returns {ID: ..., permalink: ...}
            if (resp.ID && resp.permalink) {
                toast.success('Campaign published successfully!');
                // Update the campaign context with WordPress info
                campaignDispatch({
                    type: 'set_wordpress',
                    payload: { 
                        wordpress_id: resp.ID, 
                        wordpress_permalink: resp.permalink 
                    },
                });
                // Clear the photo sync flag since we just synced
                campaignDispatch({
                    type: 'set_needs_photo_sync',
                    payload: false,
                });
            } else {
                toast.error('Failed to publish campaign. Please try again.');
            }
            
            setPublishing(false);
        } catch (e) {
            toast.dismiss();
            console.error('Error publishing campaign:', e);
            toast.error('There was an error publishing your campaign');
            setPublishing(false);
        }
    };

    const handleRepublishCampaign = async () => {
        if (!window.confirm(
            'This will verify if your WordPress page exists. If not found, it will create a new WordPress post with a new URL. Do you want to continue?'
        )) {
            return;
        }

        try {
            setRepublishing(true);
            const loadingToast = toast.loading('Verifying and republishing campaign...');
            
            const resp = await republishCampaign(campaign.campaignDetail.campaign_id, false);
            
            toast.dismiss(loadingToast);
            
            // Check for standard API error (detail field)
            if (resp.detail) {
                toast.error(resp.detail);
                setRepublishing(false);
                return;
            }
            
            // Check for WordPress sync error (code/message structure)
            if (resp.code && resp.message) {
                console.error('WordPress sync error:', resp);
                toast.error(resp.message);
                setRepublishing(false);
                return;
            }
            
            // WordPress returns {ID: ..., permalink: ...}
            if (resp.ID && resp.permalink) {
                toast.success('Campaign republished successfully!');
                // Update the campaign context with WordPress info
                campaignDispatch({
                    type: 'set_wordpress',
                    payload: { 
                        wordpress_id: resp.ID, 
                        wordpress_permalink: resp.permalink 
                    },
                });
                // Clear the photo sync flag since we just synced
                campaignDispatch({
                    type: 'set_needs_photo_sync',
                    payload: false,
                });
                
                // Refresh campaign data to get updated info
                await getMyCampaign();
            } else {
                toast.error('Failed to republish campaign. Please try again.');
            }
            
            setRepublishing(false);
        } catch (e) {
            toast.dismiss();
            console.error('Error republishing campaign:', e);
            toast.error('There was an error republishing your campaign');
            setRepublishing(false);
        }
    };

    const handleAddLink = async () => {
        const regMatch =
            /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#()?&//=]*)/;
        const isValidUrl = (url) => {
            return regMatch.test(url);
        };
        try {
            const link = addLinkRef.current.value;
            if (!link || link == '') {
                return;
            }
            const isValid = isValidUrl(link);
            if (isValid) {
                const newList = [...new Set([...addLinks, link])];
                setAddLinks(newList);
                addLinkRef.current.value = '';
                addLinkRef.current.focus();
            } else {
                console.log('invalid url', link);
            }
        } catch (e) {
            console.log('e: ', e);
        }
    };

    const FormActions = () => {
        const enablePublishFeature = import.meta.env.VITE_ENABLE_PUBLISH_CAMPAIGN === 'true';
        const isPublished = !!(campaign?.campaignDetail?.wordpress_id && campaign?.campaignDetail?.wordpress_permalink);
        const showViewButton = enablePublishFeature 
            ? isPublished 
            : ['Disbursable', 'Funded'].includes(campaign?.campaignDetail?.campaign_status);
        
        return (
            <>
                <Button
                    color={
                        campaign?.campaignDetail?.campaign_status
                            ? 'link mx-0'
                            : 'outline-dark'
                    }
                    onClick={() => history.push('/dashboard')}
                    disabled={isSubmitting || publishing || republishing}
                >
                    Cancel
                </Button>
                {showViewButton ? (
                    <Button
                        color='outline-dark'
                        onClick={() => {
                            if (enablePublishFeature) {
                                // Use wordpress_permalink if available, otherwise construct URL with wordpress_id
                                const wpUrl = campaign?.campaignDetail?.wordpress_permalink 
                                    || `${import.meta.env.VITE_WP_URL}/?p=${campaign?.campaignDetail?.wordpress_id}`;
                                window.open(wpUrl, '_blank');
                            } else {
                                // Legacy URL pattern using campaign_id
                                window.open(
                                    `${import.meta.env.VITE_WP_URL}/campaign/${campaign?.campaignDetail?.campaign_id}`,
                                    '_blank'
                                );
                            }
                        }}
                        disabled={isSubmitting || publishing || republishing}
                    >
                        View
                    </Button>
                ) : enablePublishFeature ? (
                    <Button
                        color='outline-dark'
                        onClick={handlePublishCampaign}
                        disabled={isSubmitting || publishing || republishing || !isValid}
                    >
                        {publishing ? 'Publishing...' : 'Publish'}
                    </Button>
                ) : (
                    <></>
                )}

                {enablePublishFeature && isPublished && (
                    <Button
                        color='outline-dark'
                        onClick={handleRepublishCampaign}
                        disabled={isSubmitting || publishing || republishing}
                    >
                        {republishing ? 'Republishing...' : 'Republish'}
                    </Button>
                )}

                <Button
                    color='primary'
                    type='submit'
                    disabled={isSubmitting || publishing || republishing}
                    onClick={() => handleSubmit(onSubmit)()}
                >
                    {isSubmitting ? `Saving` : `Save`}
                </Button>
            </>
        );
    };

    const handleNewAlbum = async () => {
        if (newAlbumName === '') {
            return;
        }
        try {
            setSavingAlbum(true);
            const newAlbum = await createAlbum(
                newAlbumName,
                campaign.campaignDetail.campaign_id
            );
            if (newAlbum.detail) {
                throw new Error(newAlbum.detail);
            }
            const newAlbumList = [newAlbum, ...campaignAlbums];
            setCampaignAlbums(newAlbumList);
            setOpen('0');
            setAddAlbum(false);
            setSavingAlbum(false);
        } catch (e) {
            console.log('e: ', e);
            setSavingAlbum(false);
        }
    };

    const handleDeleteAlbum = async (album) => {
        console.log('album: ', album);
        try {
            setDeletingAlbum(true);
            const resp = await deleteAlbum(
                album.id,
                campaign.campaignDetail.campaign_id
            );
            // if (resp.detail) {
            //   throw new Error(resp.detail);
            // }
            // const albumIndex = campaignAlbums.findIndex((x) => x.id === album.id);
            setOpen(null);
            const newAlbumList = campaignAlbums.filter(
                (x) => x.id !== album.id
            );
            setCampaignAlbums(newAlbumList);

            setDeleteAlbum(null);
            setDeletingAlbum(false);
        } catch (e) {
            console.log('e: ', e);
            setDeletingAlbum(false);
        }
    };

    // const update_photo = getValues('update_photo');

    const updatePhoto = (update: boolean) => {
        setValue('update_photo', update);
        // Track in context that photo needs to be synced to WordPress
        if (update) {
            campaignDispatch({
                type: 'set_needs_photo_sync',
                payload: true,
            });
        }
    };

    const lastUpdated = moment(campaign.campaignDetail?.updated_date).format(
        'MM/DD/YY'
    );

    return (
        <div className='container__inner'>
            <div className='mb-5'>
                <Row>
                    <Col xl={6}>
                        <h1 className='h2'>Edit Campaign</h1>
                    </Col>
                    <Col
                        xl={6}
                        className='d-none d-md-flex justify-content-xl-end align-items-start'
                    >
                        <FormActions />
                    </Col>
                </Row>
            </div>
            <Row>
                <Col lg={8}>
                    <h4>Campaign Info</h4>
                    <p>Last Updated {lastUpdated}</p>

                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <div className='mb-3'>
                            <Label>Profile Picture</Label>
                            <CampaignPhotoUpload updatePhoto={updatePhoto} />
                        </div>
                        <Row className='g-3'>
                            <Col lg={6}>
                                <FormGroup>
                                    <Label>Fundraising Goal</Label>
                                    {isLimited ? (
                                        <input
                                            type='text'
                                            className='form-control'
                                            value={numberFormat(
                                                campaign.campaignDetail?.goal
                                            )}
                                            disabled
                                        />
                                    ) : (
                                        <IntlCurrencyInput
                                            defaultValue={
                                                campaign.campaignDetail?.goal
                                            }
                                            onChange={(e, val, masked) => {
                                                setValue('goal', val);
                                            }}
                                            className='form-control'
                                            config={currencyConfig}
                                        />
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <CheckboxInput
                            name='show_goal_bar'
                            label='Show fundraising thermometer on page'
                            control={control}
                            disabled={isLimited}
                        />
                        {/* <CheckboxInput
              name='show_donor_roll'
              label='Show donor roll on page'
              control={control}
            /> */}
                        <TextInput
                            name='campaign_title'
                            label='Campaign Page Title'
                            control={control}
                            required={true}
                            ariaRequired={true}
                        />
                        {/* <TextInput
              name='campaign_slug'
              label='Campaign URL'
              control={control}
              helperText='Making your own campaign URL makes it easy to remember and share with others'
              errors={errors?.campaign_slug?.message}
            /> */}
                        {/* <TextInput
              name='video_url'
              label='Video URL'
              helperText='Enter a YouTube or Vimeo link to display a video above your Campaign Story.'
              control={control}
            /> */}
                        {!videoUrl && (
                            <FormGroup>
                                {showVideoField && (
                                    <>
                                        <Label className='mb-1'>
                                            Video URL
                                        </Label>
                                        <Controller
                                            name='video_url'
                                            control={control}
                                            render={({ field }) => (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '8px',
                                                    }}
                                                >
                                                    <Input
                                                        type='text'
                                                        placeholder='http://'
                                                        {...field}
                                                    />

                                                    <FormText color='muted'>
                                                        Enter YouTube or Vimeo
                                                        URL
                                                    </FormText>
                                                </div>
                                            )}
                                        />
                                    </>
                                )}

                                {!showVideoField && (
                                    <Button
                                        size='sm'
                                        color='outline-dark'
                                        className='modal_cancel'
                                        type='button'
                                        onClick={
                                            () => setShowVideoField(true)
                                            // setValue(
                                            //     'video_url',
                                            //     getEmbeddedVideoUrl(
                                            //         field?.value
                                            //     )
                                            // )
                                        }
                                        // disabled={
                                        //     !field.value || !field.value.trim()
                                        // }
                                    >
                                        <MdAdd size={20} className='mb-1' />
                                        Add Video
                                    </Button>
                                )}
                            </FormGroup>
                        )}

                        {videoUrl && (
                            <div>
                                <p
                                    style={{
                                        fontWeight: 'bold',
                                        fontSize: '0.8rem',
                                        marginBottom: '5px',
                                    }}
                                >
                                    Uploaded Video
                                </p>
                                <div
                                    style={{
                                        marginTop: '10px',
                                    }}
                                >
                                    <iframe
                                        width='300px'
                                        height='200px'
                                        src={videoUrl}
                                        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                                        allowFullScreen
                                    ></iframe>
                                </div>
                                <Button
                                    size='sm'
                                    color='outline-dark'
                                    className='modal_cancel'
                                    onClick={() => {
                                        setValue('video_url', '');
                                        // setValue('video', '');
                                    }}
                                >
                                    <RxCross2
                                        size={18}
                                        style={{
                                            position: 'relative',
                                            top: '-1px',
                                        }}
                                    />
                                    Remove Video
                                </Button>
                                <p className='mt-2'>{videoUrl}</p>
                            </div>
                        )}

                        <FormGroup>
                            <Label>Campaign Story</Label>
                            <Controller
                                control={control}
                                name='story'
                                rules={{ required: true }}
                                render={({
                                    field: { onChange, value, onBlur },
                                }) => {
                                    return (
                                        <TipTapEditor
                                            initialValue={value}
                                            onChange={onChange}
                                        />
                                    );
                                }}
                            />
                        </FormGroup>
                        {/* <FormGroup>
              <Label>Help Hope Live Supporting Content</Label>
              <p className='text-muted fst-italic'>
                Ultrices nibh cursus vel eu tincidunt sed sed. Scelerisque felis
                tincidunt at vulputate neque mauris odio. Integer sagittis velit
                tellus hendrerit magnis potenti pulvinar. Massa turpis enim id
                dui sit ultricies nullam tortor. Odio aenean cras et auctor
                posuere elit penatibus maecenas gravida. At eu est nulla ante
                imperdiet netus aliquam sit. Nibh scelerisque tortor quam ipsum
                tortor congue tellus. Est turpis libero semper et ultrices
                fringilla. Viverra est tincidunt volutpat quis. Vulputate vitae
                aenean tristique gravida.
              </p>
              <Button color='outline-dark' size='sm'>
                Use Story Worksheet
              </Button>
            </FormGroup> */}
                        {/* <hr className='my-5' />
            <h4>Additional Support Links</h4>
            <FormGroup>
              <Label>URL</Label>
              <Row className='g-3 mb-3'>
                <Col xs={9}>
                  <input
                    id='altLinkInput'
                    name='altLinkInput'
                    placeholder='www.url.com/'
                    ref={addLinkRef}
                    className='form-control'
                  />
                  <small className='form-text text-muted'>
                    URL does not need to include http://
                  </small>
                </Col>
                <Col>
                  <Button color='outline-dark' onClick={handleAddLink}>
                    + Add
                  </Button>
                </Col>
              </Row>
              {addLinks?.map((link, i) => {
                return (
                  <div className='mb-2' key={i}>
                    <Badge
                      color='primary'
                      className='text-dark'
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        setAddLinks(addLinks.filter((x) => x !== link));
                      }}
                    >
                      {link}
                      <span className='ms-2'>{renderIcon('close')}</span>
                    </Badge>
                  </div>
                );
              })}
            </FormGroup> */}
                        <hr className='my-5' />
                        <Row className='mb-6'>
                            <Col className='d-flex align-items-start'>
                                <h4>Image Galleries</h4>
                                <Button
                                    color='outline-dark'
                                    size='sm'
                                    className='ms-4'
                                    onClick={() => {
                                        setAddAlbum(true);
                                    }}
                                >
                                    + Add Gallery
                                </Button>
                            </Col>
                        </Row>
                        {/* {addAlbum && (
              <Accordion open={open} toggle={() => toggle('album-new')}>
                <AlbumEdit album={[]} index='new' />
              </Accordion>
            )} */}
                        <Accordion
                            className='mb-5'
                            open={open || '0'}
                            toggle={() => {}}
                        >
                            {campaignAlbums.map((a, i) => {
                                return (
                                    <AlbumEdit
                                        toggle={toggle}
                                        album={a}
                                        key={i}
                                        index={i}
                                        isOpen={open === `album-${i}`}
                                        setDeleteAlbum={setDeleteAlbum}
                                        campaign_id={
                                            campaign.campaignDetail.campaign_id
                                        }
                                        editMode={editMode}
                                        setEditMode={setEditMode}
                                    />
                                );
                            })}
                        </Accordion>
                    </Form>
                </Col>
                <Col lg={4}>
                    <ImageTipsCard />
                    <br />
                    <StoryTipsCard />
                </Col>
            </Row>
            <div
                className='sticky-bottom p-4 bg-white'
                style={{
                    borderTop: '1px solid #0D0106',
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.25)',
                }}
            >
                <div className='d-md-block'>
                    <FormActions />
                </div>
            </div>
            <Modal centered isOpen={addAlbum} toggle={() => setAddAlbum(false)}>
                <ModalBody>
                    <Form>
                        <FormGroup className='mb-5'>
                            <Label>Create New Gallery</Label>
                            <Input
                                id='album-name'
                                name='album-name'
                                onChange={(e) =>
                                    setNewAlbumName(e.target.value)
                                }
                            />
                        </FormGroup>
                        <Button
                            color='outline-dark'
                            onClick={() => setAddAlbum(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color='primary'
                            onClick={handleNewAlbum}
                            disabled={savingAlbum}
                        >
                            {savingAlbum ? `Saving` : `Save`}
                        </Button>
                    </Form>
                </ModalBody>
            </Modal>
            <Modal
                centered
                isOpen={!!albumToDelete}
                toggle={() => setDeleteAlbum(null)}
            >
                <ModalBody>
                    <h3 className='text-modal modal__title'>
                        Are you sure you want to delete this gallery?
                    </h3>
                    <Button
                        color='outline-dark'
                        onClick={() => setDeleteAlbum(null)}
                        disabled={deletingAlbum}
                    >
                        Cancel
                    </Button>
                    <Button
                        color='danger'
                        onClick={() => handleDeleteAlbum(albumToDelete)}
                        disabled={deletingAlbum}
                    >
                        {deletingAlbum ? `Deleting...` : `Delete`}
                    </Button>
                </ModalBody>
            </Modal>
        </div>
    );
};
